import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type {
  CredentialTestResult,
  Partner,
  PartnerChannel,
  PartnerDetail,
  SyncResult,
} from '@/lib/types';

/**
 * The partner chain. The list comes back flat with a `parentId` on every row —
 * the tree is built in `PartnerTree`, because a nested response would have to
 * pick a depth and the chain's depth is a commercial decision.
 */

interface PartnerInput {
  name: string;
  slug: string;
  parentId?: string | null;
  contactEmail?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export function usePartners() {
  return useQuery({
    queryKey: ['partners'],
    queryFn: () => apiFetch<{ data: Partner[] }>('/v1/admin/partners'),
  });
}

export function usePartner(id: string) {
  return useQuery({
    queryKey: ['partners', id],
    queryFn: () => apiFetch<PartnerDetail>(`/v1/admin/partners/${id}`),
    enabled: Boolean(id),
  });
}

export function usePartnerChannels(id: string) {
  return useQuery({
    queryKey: ['partners', id, 'channels'],
    queryFn: () => apiFetch<{ data: PartnerChannel[] }>(`/v1/admin/partners/${id}/channels`),
    enabled: Boolean(id),
  });
}

/** Everything a partner write touches, invalidated in one place. */
function usePartnerInvalidation(id?: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['partners'] });
    if (id) queryClient.invalidateQueries({ queryKey: ['partners', id] });
  };
}

export function useCreatePartner() {
  const invalidate = usePartnerInvalidation();

  return useMutation({
    mutationFn: (data: PartnerInput) =>
      apiFetch<Partner>('/v1/admin/partners', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: invalidate,
  });
}

export function useUpdatePartner(id: string) {
  const invalidate = usePartnerInvalidation(id);

  return useMutation({
    mutationFn: (data: Partial<PartnerInput>) =>
      apiFetch<Partner>(`/v1/admin/partners/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: invalidate,
  });
}

export function useDeletePartner() {
  const invalidate = usePartnerInvalidation();

  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/v1/admin/partners/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useSetCredential(id: string) {
  const invalidate = usePartnerInvalidation(id);

  return useMutation({
    mutationFn: (token: string) =>
      apiFetch<{ hint: string }>(`/v1/admin/partners/${id}/credential`, {
        method: 'PUT',
        body: JSON.stringify({ token }),
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveCredential(id: string) {
  const invalidate = usePartnerInvalidation(id);

  return useMutation({
    mutationFn: () => apiFetch<void>(`/v1/admin/partners/${id}/credential`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useTestCredential(id: string) {
  const invalidate = usePartnerInvalidation(id);

  return useMutation({
    mutationFn: () =>
      apiFetch<CredentialTestResult>(`/v1/admin/partners/${id}/credential/test`, {
        method: 'POST',
      }),
    onSuccess: invalidate,
  });
}

/** Pulls channels and devices from the provider, then refreshes both lists. */
export function useSyncInventory(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiFetch<SyncResult>(`/v1/admin/partners/${id}/sync`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
}
